<script lang="ts">
  import Badge from '../common/Badge.svelte';
  import Button from '../common/Button.svelte';
  import type { Rule } from '../../types/rule';

  let {
    rule,
    categories = [],
    people = [],
    isEditing = false,
    editValues = $bindable({} as Rule),
    loading = false,
    applyingRule = false,
    onEdit,
    onSave,
    onCancel,
    onToggleEnabled,
    onApply,
    onDelete,
    onKeydown
  }: {
    rule: Rule;
    categories: string[];
    people: string[];
    isEditing?: boolean;
    editValues?: Rule;
    loading?: boolean;
    applyingRule?: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onToggleEnabled: () => void;
    onApply: () => void;
    onDelete: () => void;
    onKeydown: (event: KeyboardEvent) => void;
  } = $props();
</script>

<div class="table-row" class:editing={isEditing} class:disabled={!rule.enabled}>
  {#if isEditing}
    <div class="col-pattern">
      <input
        type="text"
        bind:value={editValues.pattern}
        onkeydown={onKeydown}
        class="edit-input"
        autofocus
      />
    </div>
    <div class="col-display">
      <input
        type="text"
        bind:value={editValues.normalizedName}
        placeholder="Optional"
        class="edit-input"
      />
    </div>
    <div class="col-category">
      <select bind:value={editValues.category} class="edit-input">
        {#each categories as category}
          <option value={category}>{category}</option>
        {/each}
      </select>
    </div>
    <div class="col-person">
      <select bind:value={editValues.person} class="edit-input">
        <option value={null}>None</option>
        {#each people as person}
          <option value={person}>{person}</option>
        {/each}
      </select>
    </div>
    <div class="col-flags">
      <div class="flags-edit">
        <label class="flag-checkbox">
          <input type="checkbox" bind:checked={editValues.markAsRecurring} />
          <span>Recurring</span>
        </label>
        {#if editValues.markAsRecurring}
          <select bind:value={editValues.recurringType} class="edit-input-sm">
            <option value="">Type...</option>
            <option value="subscription">Sub</option>
            <option value="bill">Bill</option>
            <option value="income">Income</option>
            <option value="transfer">Transfer</option>
          </select>
        {/if}
        <label class="flag-checkbox">
          <input type="checkbox" bind:checked={editValues.excludeFromAnalytics} />
          <span>Exclude</span>
        </label>
      </div>
    </div>
    <div class="col-stats">{rule.appliedCount || 0}</div>
    <div class="col-enabled">
      <input type="checkbox" bind:checked={editValues.enabled} />
    </div>
    <div class="col-actions">
      <Button variant="success" size="sm" onclick={onSave} disabled={loading}>Save</Button>
      <Button variant="secondary" size="sm" onclick={onCancel} disabled={loading}>Cancel</Button>
    </div>
  {:else}
    <div class="col-pattern">{rule.pattern}</div>
    <div class="col-display">
      {#if rule.normalizedName}
        <span class="normalized-name">{rule.normalizedName}</span>
      {:else}
        <span class="text-muted">-</span>
      {/if}
    </div>
    <div class="col-category">
      <Badge variant="category">{rule.category}</Badge>
    </div>
    <div class="col-person">{rule.person || '-'}</div>
    <div class="col-flags">
      <div class="flags-display">
        {#if rule.markAsRecurring}
          <span class="flag-badge recurring" title={rule.recurringType || 'Recurring'}>
            {rule.recurringType === 'subscription' ? '🔄' : rule.recurringType === 'bill' ? '📄' : rule.recurringType === 'income' ? '💰' : rule.recurringType === 'transfer' ? '↔️' : '🔁'}
          </span>
        {/if}
        {#if rule.excludeFromAnalytics}
          <span class="flag-badge excluded" title="Excluded from analytics">🚫</span>
        {/if}
      </div>
    </div>
    <div class="col-stats">{rule.appliedCount || 0}x</div>
    <div class="col-enabled">
      <input
        type="checkbox"
        checked={rule.enabled}
        onchange={onToggleEnabled}
        disabled={loading}
      />
    </div>
    <div class="col-actions">
      <Button variant="primary" size="sm" onclick={onEdit} disabled={loading}>Edit</Button>
      <Button variant="warning" size="sm" onclick={onApply} disabled={loading || applyingRule}>
        {applyingRule ? 'Applying...' : 'Apply'}
      </Button>
      <Button variant="danger" size="sm" onclick={onDelete} disabled={loading}>Delete</Button>
    </div>
  {/if}
</div>

<style lang="scss">
  .table-row {
    display: grid;
    grid-template-columns: 1.5fr 1.5fr 1.2fr 0.8fr 1fr 0.6fr 0.6fr 2fr;
    gap: var(--spacing-md);
    align-items: center;
    padding: var(--spacing-md);
    font-size: var(--font-size-sm);
    border-bottom: 1px solid var(--color-border);

    &:hover:not(.editing) {
      background: var(--color-background-secondary);
    }

    &.editing {
      background: var(--color-primary-light);
    }

    &.disabled {
      opacity: 0.6;
    }
  }

  .edit-input {
    width: 100%;
    padding: var(--spacing-sm);
    border: 1px solid var(--color-primary-bright);
    border-radius: var(--spacing-xs);
    font-size: var(--font-size-sm);

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }

  .col-actions {
    display: flex;
    gap: var(--spacing-sm);
    justify-content: flex-end;
  }

  // Flags display and editing
  .flags-display {
    display: flex;
    gap: var(--spacing-sm);
    align-items: center;
  }

  .flag-badge {
    display: inline-block;
    font-size: var(--font-size-base);
    cursor: help;

    &.recurring {
      opacity: 0.9;
    }

    &.excluded {
      opacity: 0.7;
    }
  }

  .flags-edit {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    font-size: var(--font-size-xxs);

    .flag-checkbox {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);

      input[type="checkbox"] {
        margin: 0;
      }

      span {
        font-size: var(--font-size-xxs);
      }
    }

    .edit-input-sm {
      padding: var(--spacing-xs);
      border: 1px solid var(--color-primary-bright);
      border-radius: var(--spacing-xs);
      font-size: var(--font-size-xxs);
    }
  }

  .normalized-name {
    color: var(--color-success-hover);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-xs);
  }

  .text-muted {
    color: var(--color-text-muted);
  }
</style>
